import React from 'react'
import track from 'react-tracking'
import { Link } from 'react-router-dom'
import BEMHelper from '@ynap/bem-helper'
import { Hero } from '@ycos/component-hero/src/hero'
import ProgressBar from './ProgressBar/ProgressBar'
import { extractGreetingFromTime } from '../../utils'
import type { LoyaltyHeroProps } from './types'

const bem = new BEMHelper('LoyaltyHero')

export const OptedInContent = (props: LoyaltyHeroProps) => {
    const {
        tracking,
        locale,
        userFirstName = '',
        userLastName = '',
        userTitle = '',
        messages,
        currentTier,
        nextTier,
        amountSpent,
        amountPending,
        progress: {
            value: progress = 0,
            intervalMin: progressIntervalMin,
            intervalMax: progressIntervalMax,
            status: progressStatus = 'NONE',
        } = {},
        isStaff,
        colorScheme = 'dark',
        isTierExpiring = false,
        showRewardsPageLink,
        isRTL = false,
    } = props
    const { banner: bannerMessages, tier: tierMessages } = messages?.loyalty || {}
    const localizedTierName = currentTier && tierMessages?.[currentTier]?.()
    const localizedNextTierName = nextTier && tierMessages?.[nextTier]?.()
    const greeting =
        ['eip', 'eipplatinum'].includes(currentTier)
            ? extractGreetingFromTime(messages, null, userLastName, userTitle, userFirstName)
            : messages?.loyalty?.banner?.greeting({ name: userFirstName })

    const showProgress = !isStaff && progressStatus && ['PROGRESSING', 'RETAINING'].includes(progressStatus)
    const progressDescription = bannerMessages?.progressLabel({
        tier: localizedNextTierName,
        status: isTierExpiring ? 'EXPIRING' : progressStatus,
    })
    const annualSpend = bannerMessages.annualSpend({
        pending: amountPending ? amountPending : false,
    })

    return (
        <section className={bem('foreground', colorScheme)} dir={isRTL ? 'rtl' : 'ltr'}>
            {userFirstName ? <span className={bem('greeting')}>{greeting}</span> : null}
            <div className={bem('tierInfo')}>
                <h1 className={bem('title')}>{localizedTierName}</h1>
                {showProgress ? (
                    <>
                        <ProgressBar
                            className={bem('progress')}
                            title={progressDescription}
                            intervalMinLabel={progressIntervalMin}
                            intervalMaxLabel={progressIntervalMax}
                            value={progress}
                            colorScheme={colorScheme}
                        />
                        <p id="amountSpent" className={bem('amountSpent')}>
                            {amountSpent}
                        </p>
                        <label htmlFor="amountSpent" className={bem('spendingInfo')}>
                            {annualSpend}
                        </label>
                    </>
                ) : null}
            </div>
            <div className={bem('footer')}>
                {showRewardsPageLink ? (
                    <Link
                        className={bem('rewardsPagelink', colorScheme)}
                        to={`/${locale.id}/account/rewards`}
                        onClick={() => {
                            tracking.trackEvent({ event: 'select_loyalty_discover_more' })
                        }}
                        rel="noopener noreferrer"
                    >
                        {bannerMessages.gotoRewardsCTA({ tier: localizedTierName })}
                    </Link>
                ) : (
                    <span className={bem('expirationNotice')}>
                        {bannerMessages.expirationNotice({ year: new Date().getFullYear() })}
                    </span>
                )}
            </div>
        </section>
    )
}

export const OptedOutContent = (props: LoyaltyHeroProps) => {
    const { userLastName, userTitle, userFirstName, messages, isStaff, colorScheme = 'dark' } = props

    return (
        <section className={bem('foreground', { light: colorScheme === 'light', plainGreeting: true })}>
            <h1 className={bem('greeting', null, 'center')}>
                {messages?.loyalty?.banner.welcome({
                    lastName: userLastName,
                    title: userTitle,
                    firstName: userFirstName,
                })}
            </h1>
            {isStaff ? <span className={bem('badge')}>{messages?.loyalty?.banner.staffBadge()}</span> : null}
        </section>
    )
}

const LoyaltyHero = (props: LoyaltyHeroProps) => {
    const { loyaltyConsent, currentTier, picturesAndMedia } = props

    const ForegroundContent = loyaltyConsent && currentTier ? OptedInContent : OptedOutContent
    const [mobileHeight, desktopHeight] = loyaltyConsent && currentTier ? ['307px', '400px'] : ['200px', '320px']
    return (
        <Hero
            mobileHeight={mobileHeight}
            desktopHeight={desktopHeight}
            config={{ imageSphere: { quality: 85 } }}
            opacity="0"
            imageProps={{ lazy: false, isPictureFullWidth: true }}
            isLoyaltyHeader={true}
            foreground={<ForegroundContent {...props} />}
            pictures={picturesAndMedia}
        />
    )
}

LoyaltyHero.layoutVariant = ['isc-loyalty-tier-banner']

export default track()(LoyaltyHero)
